import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import type { DateRangeType } from "@lemmonhq/types";

dayjs.extend(isoWeek);
dayjs.extend(quarterOfYear);

export type { DateRangeType };

export type DateRangeBounds = { startDate: string; endDate: string };

export type DateRangeOption = {
  value: DateRangeType;
  label: string;
};

/**
 * Pure date math for the closed-vocabulary date range presets shared by every
 * date range picker in the app. Lives here (not in `packages/uikit`) so
 * non-UI callers (report defaults, PDF/export workflows) can compute the same
 * ranges without depending on a Vue component library.
 */
export const getDateRange = (type: DateRangeType): DateRangeBounds => {
  const today = dayjs();
  switch (type) {
    case "today":
      return {
        startDate: today.format("YYYY-MM-DD"),
        endDate: today.format("YYYY-MM-DD"),
      };
    case "yesterday":
      return {
        startDate: today.subtract(1, "day").format("YYYY-MM-DD"),
        endDate: today.subtract(1, "day").format("YYYY-MM-DD"),
      };
    case "this_week":
      return {
        startDate: today.startOf("isoWeek").format("YYYY-MM-DD"),
        endDate: today.startOf("isoWeek").add(1, "week").format("YYYY-MM-DD"),
      };
    case "last_week": {
      const start = today.subtract(1, "week").startOf("isoWeek");
      return {
        startDate: start.format("YYYY-MM-DD"),
        endDate: start.endOf("isoWeek").format("YYYY-MM-DD"),
      };
    }
    case "this_month":
      return {
        startDate: today.startOf("month").format("YYYY-MM-DD"),
        endDate: today.format("YYYY-MM-DD"),
      };
    case "last_month": {
      const start = today.subtract(1, "month").startOf("month");
      return {
        startDate: start.format("YYYY-MM-DD"),
        endDate: start.endOf("month").format("YYYY-MM-DD"),
      };
    }
    case "this_quarter":
      return {
        startDate: today.startOf("quarter").format("YYYY-MM-DD"),
        endDate: today.format("YYYY-MM-DD"),
      };
    case "last_quarter": {
      const start = today.subtract(1, "quarter").startOf("quarter");
      return {
        startDate: start.format("YYYY-MM-DD"),
        endDate: start.endOf("quarter").format("YYYY-MM-DD"),
      };
    }
    case "this_year":
      return {
        startDate: today.startOf("year").format("YYYY-MM-DD"),
        endDate: today.format("YYYY-MM-DD"),
      };
    case "last_year": {
      const start = today.subtract(1, "year").startOf("year");
      return {
        startDate: start.format("YYYY-MM-DD"),
        endDate: start.endOf("year").format("YYYY-MM-DD"),
      };
    }
    case "last_12m":
      return {
        startDate: today.subtract(12, "month").format("YYYY-MM-DD"),
        endDate: today.format("YYYY-MM-DD"),
      };
    default:
      return {
        startDate: today.subtract(1, "month").format("YYYY-MM-DD"),
        endDate: today.format("YYYY-MM-DD"),
      };
  }
};

/** Preset option order shown in every date range picker's dropdown. */
export const DATE_RANGE_PRESET_VALUES: DateRangeType[] = [
  "today",
  "yesterday",
  "this_week",
  "last_week",
  "this_month",
  "last_month",
  "this_quarter",
  "last_quarter",
  "this_year",
  "last_year",
  "last_12m",
];
